import gsap from 'gsap';

import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';
import { cleanup } from './dot-cms-aniamtion.helper';

const activeTweens = new WeakMap<Element, gsap.core.Tween | null>();

// Track active event listeners per element to ensure proper cleanup
const activeClickListeners = new WeakMap<Element, (event: Event) => void>();

// Helper function to remove existing click listener if it exists
function removeExistingClickListener(el: Element) {
  const existingListener = activeClickListeners.get(el);
  if (existingListener) {
    el.removeEventListener('click', existingListener);
    activeClickListeners.delete(el);
  }
}

// Helper function to add new click listener and track it
function addClickListener(el: Element, listener: (event: Event) => void) {
  removeExistingClickListener(el); // Remove any existing listener first
  el.addEventListener('click', listener);
  activeClickListeners.set(el, listener); // Track the new listener
}

export const clickAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween | null>
> = {
  'click-expand': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        scale: s.scale ?? 1.5,
        overwrite: 'auto',
        onComplete: () =>
          gsap.to(el, {
            scale: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-shrink': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        scale: s.scale ?? 0.5,
        overwrite: 'auto',
        ...opts,
        onComplete: () =>
          gsap.to(el, {
            scale: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-move': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      const angleInRadians = ((s.angle ?? 0) * Math.PI) / 180;
      return gsap.to(el, {
        x: Math.cos(angleInRadians) * 40,
        y: Math.sin(angleInRadians) * 40,
        overwrite: 'auto',
        onComplete: () =>
          gsap.to(el, {
            x: 0,
            y: 0,
            scale: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-fade': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        opacity: s.opacity ?? 0,
        overwrite: 'auto',
        ...opts,
        onComplete: () =>
          gsap.to(el, {
            opacity: 1,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-rotate': (_s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        rotation: 360,
        overwrite: 'auto',
        onComplete: () =>
          gsap.to(el, {
            rotation: 0,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-skew': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        skewX: s.angle ?? 30,
        overwrite: 'auto',
        onComplete: () =>
          gsap.to(el, {
            skewX: 0,
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
        ...opts,
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-color': (s, el, opts = {}) => {
    cleanup(el);
    console.log('color', s);
    let tween: gsap.core.Tween | null = null;
    const originalColor = window.getComputedStyle(el).color;
    gsap.set(el, { color: '#000' });

    function createTween() {
      const hueRotateValue = s.fillColor
        ? parseInt(s.fillColor.replace('#', ''), 16) % 360 // Convert hex to int and mod 360 for hue rotation
        : 0;
      return gsap.to(el, {
        color: s.fillColor,
        filter: s.fillColor ? `hue-rotate(${hueRotateValue}deg)` : 'none',
        overwrite: 'auto',
        ...opts,
        onComplete: () =>
          gsap.to(el, {
            fill: originalColor,
            filter: 'none',
            duration: 0.3,
            ease: 'power1.out',
            overwrite: 'auto',
          }),
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-twist': (s, el, opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      return gsap.to(el, {
        rotation: s.angle ?? 45,
        scale: s.scale ?? 1.2,
        ...opts,
        onComplete: () => {
          gsap.to(el, { rotation: 0, duration: 0.3, scale: 1, overwrite: 'auto' });
        },
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },

  'click-appear': (s, el, _opts = {}) => {
    cleanup(el);

    let tween: gsap.core.Tween | null = null;

    function createTween() {
      // First, immediately set the element to the starting state (invisible)
      return gsap.to(el, {
        opacity: 0,
        delay: s.delay ?? 0,
        duration: 0.8,
        onComplete: () => {
          gsap.set(el, { opacity: 1 });
        },
      });
    }

    function onClick() {
      const existingTween = activeTweens.get(el);
      if (existingTween?.isActive()) {
        return;
      }
      existingTween?.kill();

      tween = createTween();
      activeTweens.set(el, tween);
    }

    addClickListener(el, onClick);
    return tween;
  },
};
